import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { SignupStateService } from '../../services/signup-state.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import {
  ORIGEN_FONDOS,
  INGRESOS_MENSUALES,
  PATRIMONIO,
  ACTIVIDADES_ECONOMICAS,
  CANTIDAD_OPERACIONES,
} from '../../constants/pep-empresa.constants';
import { PASOS_EMPRESA } from '../../constants/signup.constants';

/** Limite del monolito para las dos descripciones libres */
const MAX_DESCRIPCION = 500;

/**
 * Paso 12 (empresa): informacion del negocio.
 *
 * Actividad, origen de fondos, situacion patrimonial y volumen operacional.
 * Persiste en `data3`, igual que el registro de empresa del dashboard.
 */
@Component({
  selector: 'app-informacion-negocio-step',
  templateUrl: './informacion-negocio-step.component.html',
  styleUrls: ['./informacion-negocio-step.component.sass'],
})
export class InformacionNegocioStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  form!: FormGroup;

  origenFondos = ORIGEN_FONDOS;
  ingresosMensuales = INGRESOS_MENSUALES;
  patrimonio = PATRIMONIO;
  actividades = ACTIVIDADES_ECONOMICAS;
  cantidadOperaciones = CANTIDAD_OPERACIONES;
  maxDescripcion = MAX_DESCRIPCION;

  loading = false;

  /**
   * Este es el ultimo paso del pre-registro, asi que no navega a ninguna parte:
   * muestra su propia pantalla de cierre. Antes llamaba a `nextStep()`, que
   * calculaba un paso mas alla del total y no hacia nada — el boton respondia y
   * la pantalla no cambiaba.
   */
  completado = false;

  private userId: string | number | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    public signupState: SignupStateService,
    private companyService: CompanyRegistrationService
  ) {}

  ngOnInit(): void {
    // Prerrequisito: los datos de la empresa
    if (!this.signupState.personaJuridica) {
      this.signupState.goToStep(PASOS_EMPRESA.PERSONA_JURIDICA);
      return;
    }

    const authSub = this.store.select('auth').subscribe(({ user }: any) => {
      this.userId = user?.usuarioId ?? null;
    });
    this.subscriptions.push(authSub);

    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // ==================== FORMULARIO ====================

  private initForm(): void {
    const saved = this.signupState.informacionNegocio;

    this.form = this.fb.group({
      descripcionNegocio: [
        saved?.descripcionNegocio || '',
        [Validators.required, Validators.maxLength(MAX_DESCRIPCION)],
      ],
      descripcionOrigenFondos: [
        saved?.descripcionOrigenFondos || '',
        [Validators.required, Validators.maxLength(MAX_DESCRIPCION)],
      ],
      origenFondos: [saved?.origenFondos || '', Validators.required],
      ingresosMensuales: [saved?.ingresosMensuales || '', Validators.required],
      patrimonioTotal: [saved?.patrimonioTotal || '', Validators.required],
      patrimonioFinanciero: [saved?.patrimonioFinanciero || '', Validators.required],
      actividadEconomica: [saved?.actividadEconomica || '', Validators.required],
      cantidadOperacionesMensuales: [
        saved?.cantidadOperacionesMensuales || '',
        Validators.required,
      ],
      volumenOperacionesMensuales: [
        saved?.volumenOperacionesMensuales || '',
        Validators.required,
      ],
    });
  }

  /** Solo numeros en el volumen de operaciones */
  allowOnlyNumbers(event: KeyboardEvent): void {
    const allowed = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'Home', 'End',
    ];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^[0-9]$/.test(event.key)) event.preventDefault();
  }

  charsLeft(field: string): number {
    const value = this.form.get(field)?.value || '';
    return MAX_DESCRIPCION - value.length;
  }

  // ==================== ENVIO ====================

  async onSubmit(): Promise<void> {
    if (this.loading) return;

    if (this.form.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Completa todos los campos requeridos.');
      return;
    }

    if (!this.userId) {
      this.onError.emit(
        'No pudimos identificar la empresa. Recarga la pagina e intenta nuevamente.'
      );
      return;
    }

    this.loading = true;

    try {
      const values = this.form.getRawValue();

      // Guardar en el estado antes de la red, para no perder lo escrito
      this.signupState.setInformacionNegocio(values);

      await this.companyService.addDataToRegister(3, values, String(this.userId));

      this.onSuccess.emit('Informacion del negocio guardada');
      this.completado = true;
    } catch (error: any) {
      this.onError.emit(
        error?.message || 'Error al guardar los datos. Intenta nuevamente.'
      );
    } finally {
      this.loading = false;
    }
  }

  /**
   * Cierra el pre-registro y entra al dashboard.
   *
   * IMPORTANTE (bug del "flash del paso 1"): `completeRegistration()` hacía
   * `state.next(INITIAL_STATE)` (paso 1) ANTES de navegar, así que el wizard
   * re-renderizaba el paso 1 por un instante antes de irse al home. Ahora
   * limpiamos sólo el flag/persistencia que chequea el `AuthGuard`
   * (`clearForExit`), navegamos, y recién tras navegar reseteamos el estado
   * en memoria (`reset`), cuando el wizard ya está desmontado.
   *
   * Donde cae: `/wallet` → home. Con el cupo de pre-aprobado ya creado, el
   * usuario queda PRE-operativo (opera con tope mientras completa la
   * documentación).
   */
  entrarAlDashboard(): void {
    this.signupState.clearForExit();
    this.router.navigate(['/wallet']).then(() => this.signupState.reset());
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && control.touched;
  }

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
